import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut, Plus, Store } from 'lucide-react';
import { supabase } from '../lib/supabase';
import UserBookings from './UserBookings';
import { Movie } from '../types';

interface UserMenuProps {
  userEmail: string;
  userId: string;
  onSignOut: () => void;
  onNavigate: (page: string) => void;
  onContinuePending?: (movie: Movie, screeningId: number, bookingId: number, seatIds: string[]) => Promise<void> | void;
}

export default function UserMenu({ userEmail, userId, onSignOut, onNavigate, onContinuePending }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showBookings, setShowBookings] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchUserRole() {
      if (!userId) return;
      console.log('UserMenu: fetching role for', userId);
      const { data } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();
      const r = data?.role || null;
      console.log('UserMenu: profile role is', r);
      setUserRole(r);

      // Check if user is an assigned validator (organizer-level or event-level)
      if (r !== 'admin' && r !== 'organizer' && r !== 'validator') {
        const { count: orgValCount, error: orgErr } = await supabase
          .from('organizer_validators')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId);

        const { count: screenValCount, error: screenErr } = await supabase
          .from('screening_validators')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('active', true);

        console.log('UserMenu: validator counts', { orgValCount, screenValCount, orgErr, screenErr });

        if ((orgValCount || 0) > 0 || (screenValCount || 0) > 0) {
          console.log('UserMenu: setting role to validator');
          setUserRole('validator'); // Treat as validator for menu visibility
        }
      }
    }

    fetchUserRole();
  }, [userId]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center overflow-hidden rounded-full h-10 w-10 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:text-white transition-colors duration-300"
      >
        <User className="h-5 w-5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-black dark:ring-opacity-5">
          <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Hi, {userEmail.split('@')[0]}</div>
          <div className="py-1">
            {(userRole === 'editor' || userRole === 'organizer') && (
              <>
                <button
                  onClick={() => {
                    onNavigate('add-screening');
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-indigo-700 hover:bg-gray-100 hover:text-indigo-800 dark:text-indigo-300 dark:hover:bg-gray-700 dark:hover:text-indigo-200 transition-colors duration-300 border-b border-gray-200 dark:border-gray-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Screening
                </button>
                <button
                  onClick={() => {
                    onNavigate('add-movie');
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-indigo-700 hover:bg-gray-100 hover:text-indigo-800 dark:text-indigo-300 dark:hover:bg-gray-700 dark:hover:text-indigo-200 transition-colors duration-300 border-b border-gray-200 dark:border-gray-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Movie
                </button>
                <button
                  onClick={() => {
                    onNavigate('edit-movies');
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-indigo-700 hover:bg-gray-100 hover:text-indigo-800 dark:text-indigo-300 dark:hover:bg-gray-700 dark:hover:text-indigo-200 transition-colors duration-300 border-b border-gray-200 dark:border-gray-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Edit Movies
                </button>
                <button
                  onClick={() => {
                    onNavigate('edit-screenings');
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-indigo-700 hover:bg-gray-100 hover:text-indigo-800 dark:text-indigo-300 dark:hover:bg-gray-700 dark:hover:text-indigo-200 transition-colors duration-300 border-b border-gray-200 dark:border-gray-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Edit Screenings
                </button>
              </>
            )}
            {(userRole === 'editor' || userRole === 'admin' || userRole === 'organizer' || userRole === 'validator') && (
              <button
                onClick={() => {
                  onNavigate('validate-tickets');
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-indigo-700 hover:bg-gray-100 hover:text-indigo-800 dark:text-indigo-300 dark:hover:bg-gray-700 dark:hover:text-indigo-200 transition-colors duration-300 border-b border-gray-200 dark:border-gray-700"
              >
                Validate Tickets
              </button>
            )}
            {(userRole === 'organizer' || userRole === 'admin') && (
              <button
                onClick={() => {
                  onNavigate('organizer-panel');
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-indigo-700 hover:bg-gray-100 hover:text-indigo-800 dark:text-indigo-300 dark:hover:bg-gray-700 dark:hover:text-indigo-200 transition-colors duration-300 border-b border-gray-200 dark:border-gray-700"
              >
                Organizer Panel
              </button>
            )}
            {userRole !== 'organizer' && userRole !== 'admin' && (
              <button
                onClick={() => {
                  onNavigate('host-events');
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-indigo-700 hover:bg-gray-100 hover:text-indigo-800 dark:text-indigo-300 dark:hover:bg-gray-700 dark:hover:text-indigo-200 transition-colors duration-300 border-b border-gray-200 dark:border-gray-700"
              >
                Become Organizer
              </button>
            )}
            {(userRole === 'admin') && (
              <button
                onClick={() => {
                  onNavigate('admin-panel');
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-indigo-700 hover:bg-gray-100 hover:text-indigo-800 dark:text-indigo-300 dark:hover:bg-gray-700 dark:hover:text-indigo-200 transition-colors duration-300 border-b border-gray-200 dark:border-gray-700"
              >
                Admin Panel
              </button>
            )}
            <button
              onClick={() => {
                onNavigate('vendor/dashboard');
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-rose-600 hover:bg-gray-100 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-gray-700 dark:hover:text-rose-300 transition-colors duration-300 border-b border-gray-200 dark:border-gray-700"
            >
              <Store className="h-4 w-4 mr-2" />
              Vendor Dashboard
            </button>
            <button
              onClick={() => {
                setShowBookings(true);
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white transition-colors duration-300"
            >
              <User className="h-4 w-4 mr-2" />
              My Bookings
            </button>
            <button
              onClick={() => {
                onSignOut();
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white transition-colors duration-300"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </button>
          </div>
        </div>
      )}

      {showBookings && <UserBookings onClose={() => setShowBookings(false)} onContinuePending={onContinuePending} />}
    </div>
  );
}
